import { useTranslation } from '../i18n';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

import { userPath, adminPath, hasUserMode, hasAdminMode, USER_BASE_PATH } from '../config/paths';
import { syncSignedInHint } from '../utils/signedInHint';

/**
 * 无权限提示页（纯 HTML，零 UI 库依赖）。
 * 之前当"已登录但角色不匹配"且没有可跳转的目标时，代码会 `<Navigate to="/dashboard">`，
 * 而 /dashboard 又被同一个 PrivateRoute 守卫 → 无限重定向 → #root 渲染为空 → 纯白屏且无报错。
 * 这里改为渲染一个明确的提示页，彻底杜绝死循环白屏，并告知当前角色、提供重新登录。
 */
const AccessDenied = ({ role }) => {
    const { t } = useTranslation();
    const relogin = () => {
        try {
            localStorage.removeItem('auth_token');
            syncSignedInHint();
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user_info');
        } catch (e) { /* noop */ }
        window.location.href = '/login';
    };
    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: '48px 16px', boxSizing: 'border-box', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif' }}>
            <div style={{ width: '100%', maxWidth: 520, background: '#fff', border: '1px solid #ffe58f', borderRadius: 10, padding: 28, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: 22, fontWeight: 600, color: '#d48806', marginBottom: 10 }}>{t('You do not have access to the admin console')}</div>
                <div style={{ color: '#595959', fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
                    {t('The current account\'s role is ')} <strong style={{ color: '#cf1322' }}>{role ? String(role) : t('(Unknown/empty)')}</strong>{t(', you do not have administrator (admin) permissions and cannot access this console.')}<br />
                    {t('Please sign in again with an administrator account.')}
                </div>
                <button onClick={relogin} style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #1677ff', background: '#1677ff', color: '#fff', cursor: 'pointer', fontSize: 14 }}>
                    {t('Sign in again')}
                </button>
            </div>
        </div>
    );
};

const PrivateRoute = ({ children, allowedRoles = [] }) => {
    const { t } = useTranslation();
    const { currentUser, loading } = useAuth();
    const location = useLocation();
    const isDark = localStorage.getItem('theme') === 'dark';

    if (loading) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isDark ? '#141414' : '#fff',
                zIndex: 9999
            }}>
                <object type="image/svg+xml" data="/loading.svg" width="200" height="100">
                    {t('Loading...')}
                </object>
            </div>
        );
    }

    // 用户未登录
    // Build a login URL with return_to so the user lands back here after re-auth.
    // We use a query param (instead of relying on React Router state) so that
    // a hard reload / cross-history nav still preserves the destination —
    // the apiClient 401 interceptor uses the same convention.
    if (!currentUser) {
        const here = location.pathname + (location.search || '') + (location.hash || '');
        // Don't capture meaningless paths as return_to
        const onAuthPath = /^\/(login|register|reset-password)(\/|$)/.test(location.pathname);
        const loginTo = onAuthPath ? '/login' : '/login?return_to=' + encodeURIComponent(here);
        return <Navigate to={loginTo} state={{ from: location }} replace />;
    }

    // 检查角色权限
    if (allowedRoles.length > 0 && !allowedRoles.includes(currentUser.role)) {
        // 管理员访问用户端页面：允许
        const userPrefix = USER_BASE_PATH || '';
        if (currentUser.role === 'admin' && (userPrefix === '' || location.pathname.startsWith(userPrefix))) {
            return children;
        }

        // 计算一个"安全的"重定向目标。关键：绝不能跳到一个仍被同一守卫拦截的路径，
        // 否则会造成无限重定向 → 空白 #root 白屏（本次线上事故的根因）。
        let target = null;
        if (currentUser.role === 'admin' && hasAdminMode()) {
            target = adminPath('/dashboard');
        } else if (currentUser.role !== 'admin' && hasUserMode()) {
            target = userPath('/dashboard');
        }

        // 只有存在有效目标、且该目标不等于当前路径时才跳转；否则直接展示无权限页。
        if (target && target !== location.pathname) {
            return <Navigate to={target} replace />;
        }
        return <AccessDenied role={currentUser.role} />;
    }

    return children;
};

export default PrivateRoute;
